import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { UtilService } from '../../../../../core/services/util.service';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useProfile } from '../../profile/components/ProfileContext';
import {
  Candidate,
  CandidateId,
  PersonalInfoExtractionResponse,
  ProfileUrlVerification,
} from '../models/candidates.model';
import { CandidatesService } from '../services/candidates.service';
import { CandidatesFilesService } from '../services/candidates-files.service';
import { Container } from '../../../../../core/components';
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  FileTextOutlined,
  SaveOutlined,
  MailOutlined,
  WhatsAppOutlined,
  LinkOutlined,
  SafetyCertificateOutlined,
  InboxOutlined,
  DeleteOutlined,
  SolutionOutlined,
  StarFilled,
  StarOutlined,
  StopOutlined,
  RobotOutlined,
  ToolOutlined,
  UserOutlined,
  WarningFilled,
} from '@ant-design/icons';
import {
  Avatar,
  Checkbox,
  Input,
  Select,
  Tag,
  Tabs,
  Tooltip,
  Upload,
  Spin,
  message,
  Button as AntButton,
} from 'antd';
import type { UploadProps } from 'antd';
import rawCountries from '../../../../../assets/countries.json';
import JobApplicationTableByCandidate from '../../job-application/components/JobApplicationTableByCandidate';
import CandidateFilesTable from './CandidateFilesTable';
import CandidateSkills from './CandidateSkills';
import CandidateAiSummary from './CandidateAiSummary';
import { route } from '../../../../../constants/routes';
import { jo } from '../../../../../theme/css-vars';
import './CandidateProfile.css';

interface CountryOption {
  value: string;
  label: React.ReactNode;
  dial_code: string;
}
type ClickOnlyRateProps = {
  value: number | null | undefined;
  onChange?: (val: number | null) => void;
  disabled?: boolean;
  max?: number;
  sizePx?: number;
  allowClear?: boolean;
  ariaLabel?: string;
  activeColor?: string;
  inactiveColor?: string;
};

const ClickOnlyRate: React.FC<ClickOnlyRateProps> = ({
  value,
  onChange,
  disabled = false,
  max = 5,
  sizePx = 20,
  allowClear = true,
  ariaLabel = 'rating',
  activeColor = jo.starFilled,
  inactiveColor = jo.starEmpty,
}) => {
  const current = typeof value === 'number' ? value : 0;

  const setValue = (next: number) => {
    if (disabled) return;
    const v = Math.max(0, Math.min(max, next));
    onChange?.(v === 0 ? null : v);
  };

  const handleClick = (index: number) => {
    if (disabled) return;
    const clicked = index + 1;
    const next = allowClear && clicked === current ? 0 : clicked;
    setValue(next);
  };

  return (
    <div role="radiogroup" aria-label={ariaLabel} className="flex space-x-1">
      {Array.from({ length: max }).map((_, i) => {
        const filled = i < current;
        return (
          <span
            key={i}
            onClick={() => handleClick(i)}
            style={{
              fontSize: sizePx,
              color: filled ? activeColor : inactiveColor,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {filled ? <StarFilled /> : <StarOutlined />}
          </span>
        );
      })}
    </div>
  );
};

function formatProfilePhone(value?: string, dialCode?: string) {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  const countryDigits = dialCode?.replace(/\D/g, '') || '';
  const localNumber =
    countryDigits && digits.startsWith(countryDigits)
      ? digits.slice(countryDigits.length)
      : digits;
  const formattedLocal =
    localNumber.length === 8
      ? `${localNumber.slice(0, 4)} ${localNumber.slice(4)}`
      : localNumber;
  return countryDigits
    ? `+${countryDigits} ${formattedLocal}`.trim()
    : formattedLocal;
}

const CandidateProfile = () => {
  const location = useLocation();
  const activeTab = location.state?.activeTab || '1';
  const isDeleted = location.state?.deleted || false;

  const [isMobile, setIsMobile] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [fileSpinner, setFileSpinner] = useState<boolean>(false);
  const { Dragger } = Upload;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const countryOptions: CountryOption[] = useMemo(
    () =>
      rawCountries.map((country: any) => ({
        value: country.code,
        dial_code: country.dial_code,
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={country.flag}
              alt={country.name}
              style={{
                marginRight: isMobile ? '0px' : '10px',
                width: '20px',
                height: '15px',
              }}
            />
            {!isMobile && `(${country.dial_code})`}
          </div>
        ),
      })),
    [isMobile]
  );
  const defaultCountry = countryOptions.find(
    (option) => option.value === 'CR'
  )!;
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(
    defaultCountry
  );
  const { companyId, isAdmin } = useProfile();
  const { id } = useParams();
  const navigate = useNavigate();

  const [candidate, setCandidate] = useState<CandidateId>(new CandidateId());
  const [pendingBlocked, setPendingBlocked] = useState<boolean>(
    candidate.blocked
  );
  const candidateId = id ?? null;
  const [candidateLoading, setCandidateLoading] = useState(
    Boolean(candidateId)
  );
  const [initialCandidateState, setInitialCandidateState] =
    useState<CandidateId>(new CandidateId());
  const [skills, setSkills] = useState<
    { id: string; skillName: string; skillLevel: string }[]
  >([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [errorMessages, setErrorMessages] = useState<{ [key: string]: string }>(
    {}
  );
  const [profileUrlVerification, setProfileUrlVerification] =
    useState<ProfileUrlVerification | null>(null);
  const [verifiedProfileUrl, setVerifiedProfileUrl] = useState('');
  const [isVerifyingProfileUrl, setIsVerifyingProfileUrl] = useState(false);

  const [tabGutter, setTabGutter] = useState(24);
  const [aiSummaryRefreshToken, setAiSummaryRefreshToken] = useState(0);
  const filesTableRef = useRef<{ refreshFiles: () => void } | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setTabGutter(window.innerWidth <= 425 ? 16 : 24);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const getCandidate = async () => {
      if (!companyId || !candidateId) return;
      setCandidateLoading(true);
      try {
        const candidate = await CandidatesService.GetById(candidateId);
        if (candidate) {
          if (candidate.mergedIntoCandidateId) {
            message.info(
              'This duplicate was merged into the primary candidate.'
            );
            navigate(
              route(`/candidate-profile/${candidate.mergedIntoCandidateId}`),
              {
                replace: true,
                state: { ...location.state, from: location.state?.from },
              }
            );
            return;
          }
          setCandidate(candidate);
          setInitialCandidateState(candidate);
          setSkills(candidate.skills);

          if (candidate.phoneNumber) {
            let matchedCountry = countryOptions.find((option) =>
              candidate.phoneNumber?.startsWith(`+${option.dial_code}`)
            );

            if (!matchedCountry) {
              matchedCountry = countryOptions.find((option) =>
                candidate.phoneNumber?.startsWith(option.dial_code)
              );
            }

            if (matchedCountry) {
              setSelectedCountry(matchedCountry);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching client:', error);
      } finally {
        setCandidateLoading(false);
      }
    };
    getCandidate();
  }, [companyId, candidateId, countryOptions, location.state, navigate]);

  useEffect(() => {
    if (candidateId && candidate.firstname && candidate.lastname) {
      if (
        !location.state ||
        location.state.candidateName !==
          `${candidate.firstname} ${candidate.lastname}`
      ) {
        navigate(location.pathname, {
          replace: true,
          state: {
            ...location.state,
            candidateName: `${candidate.firstname} ${candidate.lastname}`,
            activeTab: activeTab,
            fromJobApplication: location.state?.fromJobApplication,
            jobApplicationId: location.state?.jobApplicationId,
          },
        });
      }
    }
  }, [
    activeTab,
    candidate.firstname,
    candidate.lastname,
    candidateId,
    location.pathname,
    location.state,
    navigate,
  ]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'profileURL') {
      setProfileUrlVerification(null);
      setVerifiedProfileUrl('');
    }
    if (name === 'phoneNumber' && selectedCountry) {
      const numericValue = value.replace(/\D/g, '');
      const fullNumber = `+${selectedCountry.dial_code}${numericValue}`;
      setCandidate((prev) => ({ ...prev, phoneNumber: fullNumber }));
    } else {
      setCandidate((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCountryChange = (selectedOption: CountryOption | null) => {
    if (selectedOption) {
      setSelectedCountry(selectedOption);
      setCandidate((prevCandidate) => {
        let rawNumber = '';

        if (prevCandidate.phoneNumber) {
          const currentCountryCode = selectedCountry?.dial_code || '';
          if (prevCandidate.phoneNumber.startsWith(`+${currentCountryCode}`)) {
            rawNumber = prevCandidate.phoneNumber.replace(
              new RegExp(`^\\+${currentCountryCode}`),
              ''
            );
          } else if (prevCandidate.phoneNumber.startsWith(currentCountryCode)) {
            rawNumber = prevCandidate.phoneNumber.replace(
              new RegExp(`^${currentCountryCode}`),
              ''
            );
          } else {
            rawNumber = prevCandidate.phoneNumber.replace(/^\+/, '');
          }
        }

        return {
          ...prevCandidate,
          phoneNumber: `+${selectedOption.dial_code}${rawNumber}`,
        };
      });
    }
  };

  const validateCandidate = (
    candidate: Candidate
  ): { errors: string[]; messages: { [key: string]: string } } => {
    const errors: string[] = [];
    const messages: { [key: string]: string } = {};

    if (!candidate || !candidate.firstname?.trim()) {
      errors.push('firstname');
      messages.firstname = 'First name is required';
    }
    if (!candidate || !candidate.lastname?.trim()) {
      errors.push('lastname');
      messages.lastname = 'Last name is required';
    }

    if (candidate?.profileURL?.trim()) {
      try {
        const profileUrl = new URL(candidate.profileURL.trim());
        if (
          !['http:', 'https:'].includes(profileUrl.protocol) ||
          !profileUrl.hostname
        ) {
          throw new Error('Unsupported profile URL');
        }
      } catch {
        errors.push('profileURL');
        messages.profileURL =
          'Please enter a valid URL (must start with http:// or https://)';
      }
    }

    if (!candidate || !candidate.email?.trim()) {
      errors.push('email');
      messages.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      if (!emailRegex.test(candidate.email.trim())) {
        errors.push('email');
        messages.email = 'Please enter a valid email address';
      }
    }

    if (!candidate || !candidate.phoneNumber?.trim()) {
      errors.push('phoneNumber');
      messages.phoneNumber = 'Phone number is required';
    } else {
      let phoneNumberWithoutCountry = candidate.phoneNumber.trim();
      if (
        selectedCountry &&
        phoneNumberWithoutCountry.startsWith(`+${selectedCountry.dial_code}`)
      ) {
        phoneNumberWithoutCountry = phoneNumberWithoutCountry.substring(
          selectedCountry.dial_code.length + 1
        );
      } else {
        phoneNumberWithoutCountry = phoneNumberWithoutCountry.replace(
          /^\+\d+/,
          ''
        );
      }

      if (phoneNumberWithoutCountry.length < 5) {
        errors.push('phoneNumber');
        messages.phoneNumber = 'Phone number must be at least 5 digits';
      } else if (!/^\d+$/.test(phoneNumberWithoutCountry)) {
        errors.push('phoneNumber');
        messages.phoneNumber = 'Phone number must contain only numbers';
      }
    }

    return { errors, messages };
  };

  const verifyProfileUrl = async (
    url = candidate.profileURL
  ): Promise<ProfileUrlVerification | null> => {
    const trimmedUrl = url?.trim();
    if (!trimmedUrl || isVerifyingProfileUrl) {
      return null;
    }

    try {
      const parsedUrl = new URL(trimmedUrl);
      if (
        !['http:', 'https:'].includes(parsedUrl.protocol) ||
        !parsedUrl.hostname
      ) {
        throw new Error('Invalid URL');
      }
    } catch {
      setProfileUrlVerification({
        url: trimmedUrl,
        status: 'INVALID',
        statusCode: null,
        message:
          'Please enter a valid URL (must start with http:// or https://).',
        acceptable: false,
      });
      setVerifiedProfileUrl(trimmedUrl);
      setValidationErrors((previous) =>
        previous.includes('profileURL') ? previous : [...previous, 'profileURL']
      );
      setErrorMessages((previous) => ({
        ...previous,
        profileURL:
          'Please enter a valid URL (must start with http:// or https://)',
      }));
      return null;
    }

    setIsVerifyingProfileUrl(true);
    try {
      const result = await CandidatesService.VerifyProfileUrl(trimmedUrl);
      setProfileUrlVerification(result);
      setVerifiedProfileUrl(trimmedUrl);

      if (result.acceptable) {
        setValidationErrors((previous) =>
          previous.filter((field) => field !== 'profileURL')
        );
        setErrorMessages((previous) => {
          const next = { ...previous };
          delete next.profileURL;
          return next;
        });
      } else {
        setValidationErrors((previous) =>
          previous.includes('profileURL')
            ? previous
            : [...previous, 'profileURL']
        );
        setErrorMessages((previous) => ({
          ...previous,
          profileURL: result.message,
        }));
      }
      return result;
    } catch (error) {
      console.error('Unable to verify candidate profile URL:', error);
      const unavailableResult: ProfileUrlVerification = {
        url: trimmedUrl,
        status: 'UNAVAILABLE',
        statusCode: null,
        message:
          'Verification is temporarily unavailable. You can still save this URL.',
        acceptable: true,
      };
      setProfileUrlVerification(unavailableResult);
      setVerifiedProfileUrl(trimmedUrl);
      return unavailableResult;
    } finally {
      setIsVerifyingProfileUrl(false);
    }
  };

  const ExtractPersonalInfo = async (file: File) => {
    try {
      setFileSpinner(true);

      const response: PersonalInfoExtractionResponse =
        await CandidatesService.PersonalInfoExtraction(file);

      setFileSpinner(false);

      if (response) {
        let matchedCountry = null;
        if (response.country_code) {
          matchedCountry = countryOptions.find(
            (option) => option.dial_code === response.country_code
          );
        }

        let formattedPhoneNumber = '';
        if (response.phone_number && matchedCountry) {
          const cleanPhoneNumber = response.phone_number.replace(/\D/g, '');
          formattedPhoneNumber = `+${matchedCountry.dial_code}${cleanPhoneNumber}`;
        }

        setCandidate((prev) => ({
          ...prev,
          firstname: response.first_name || '',
          lastname: response.last_name || '',
          email: response.email || '',
          phoneNumber: formattedPhoneNumber,
          profileURL: response.linkedin_url || response.profile_url || '',
        }));

        if (matchedCountry) {
          setSelectedCountry(matchedCountry);
        }

        setValidationErrors([]);
        setErrorMessages({});

        UtilService.Alert(
          'Success!',
          'success',
          'CV processed successfully! Candidate information has been auto-filled.'
        );
      }
    } catch (error: any) {
      setFileSpinner(false);
      UtilService.Alert(
        'Info',
        'info',
        error.message || 'Error processing CV file'
      );
    }
  };

  useEffect(() => {
    if (candidate && candidate.blocked !== undefined) {
      setPendingBlocked(candidate.blocked);
    }
  }, [candidate]);

  const handleSave = async () => {
    const { errors, messages } = validateCandidate(candidate);
    setValidationErrors(errors);
    setErrorMessages(messages);
    if (errors.length > 0) {
      return;
    }

    const trimmedProfileUrl = candidate.profileURL?.trim();
    if (trimmedProfileUrl) {
      const currentVerification =
        verifiedProfileUrl === trimmedProfileUrl
          ? profileUrlVerification
          : await verifyProfileUrl(trimmedProfileUrl);
      if (currentVerification && !currentVerification.acceptable) {
        setValidationErrors([...errors, 'profileURL']);
        setErrorMessages({
          ...messages,
          profileURL: currentVerification.message,
        });
        return;
      }
    }

    if (!companyId) {
      console.error('Company ID is missing');
      return;
    }

    try {
      const candidateData: Partial<Candidate> = {
        firstname: candidate.firstname ?? '',
        lastname: candidate.lastname ?? '',
        email: candidate.email ?? '',
        phoneNumber: candidate.phoneNumber ?? '',
        profileURL: candidate.profileURL ?? '',
        companyId: companyId ?? '',
        active: candidate.active ?? true,
        blocked: pendingBlocked,
        rate: candidate.rate ?? null,
      };

      if (candidateId) {
        await CandidatesService.Patch(candidateData, candidateId, companyId);

        if (file) {
          try {
            await CandidatesFilesService.UploadFile(
              candidateId,
              file,
              'RESUME'
            );
            UtilService.Alert(
              'Success!',
              'success',
              'CV file uploaded successfully.'
            );
            if (filesTableRef.current?.refreshFiles) {
              filesTableRef.current.refreshFiles();
            }
          } catch (uploadError) {
            console.error('Error uploading CV file:', uploadError);
            UtilService.Alert(
              'Warning',
              'info',
              'Candidate saved, but CV upload failed.'
            );
          }
        }

        UtilService.Alert(
          'Success!',
          'success',
          'Candidate updated successfully.'
        );
        setCandidate((prev) => ({ ...prev, blocked: pendingBlocked }));
        setInitialCandidateState({ ...candidate, id: candidateId });
        /* navigate('/route(/company/candidates'), {
           replace: true,
           state: { refreshNeeded: true }, 
         });*/
        if (window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('refreshCandidatesTable'));
        }
      } else {
        const response: CandidateId = await CandidatesService.Post(
          candidateData,
          companyId
        );
        if (response && response.id) {
          if (file) {
            try {
              await CandidatesFilesService.UploadFile(
                response.id,
                file,
                'RESUME'
              );
              UtilService.Alert(
                'Success!',
                'success',
                'CV file uploaded successfully.'
              );
              if (filesTableRef.current?.refreshFiles) {
                filesTableRef.current.refreshFiles();
              }
            } catch (uploadError) {
              console.error('Error uploading CV file:', uploadError);
              UtilService.Alert(
                'Warning',
                'info',
                'Candidate saved, but CV upload failed.'
              );
            }
          }

          UtilService.Alert(
            'Success!',
            'success',
            'Candidate added successfully.'
          );
          navigate(route(`/candidate-profile/${response.id}`));

          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          throw new Error('Invalid response from server');
        }
      }
    } catch (error: any) {
      console.error('Error saving candidate:', error);

      const is409Error =
        error?.errorCode === 409 ||
        error?.response?.status === 409 ||
        (error?.message &&
          error.message.toLowerCase().includes('candidate exist'));

      if (is409Error) {
        let errorMessage =
          error?.message || 'Candidate exists on Company Records';

        if (errorMessage.includes('Candidate exist on Company Records')) {
          errorMessage = 'Candidate exists on Company Records';
        }

        UtilService.Alert('Error', 'error', errorMessage);
      } else {
        UtilService.Alert(
          'Error',
          'error',
          'An error occurred while saving the candidate.'
        );
      }
    }
  };

  const GoBack = () => {
    setFile(null);

    if (
      location.state?.fromJobApplication &&
      location.state?.jobApplicationId
    ) {
      navigate(route(`/job-applications/${location.state.jobApplicationId}`), {
        state: {
          jobName: location.state?.jobName,
          candidateName: location.state?.candidateName,
          ...(location.state?.fromDashboard ? { fromDashboard: true } : {}),
        },
      });
    } else if (location.state?.fromRecommendations && location.state?.jobId) {
      navigate(route(`/job/${location.state.jobId}`), {
        state: {
          activeTab: 'recommendations',
          jobName: location.state?.jobName,
        },
      });
    } else if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(route('/company/candidates'));
    }
  };

  const isDataUnchanged =
    JSON.stringify(candidate) === JSON.stringify(initialCandidateState) &&
    pendingBlocked === initialCandidateState.blocked;
  const isButtonDisabled = isDataUnchanged || isDeleted;

  const clearFormData = () => {
    if (candidateId) {
      setCandidate(initialCandidateState);
      setSkills(initialCandidateState.skills || []);

      if (initialCandidateState.phoneNumber) {
        let matchedCountry = countryOptions.find((option) =>
          initialCandidateState.phoneNumber?.startsWith(`+${option.dial_code}`)
        );

        if (!matchedCountry) {
          matchedCountry = countryOptions.find((option) =>
            initialCandidateState.phoneNumber?.startsWith(option.dial_code)
          );
        }

        if (matchedCountry) {
          setSelectedCountry(matchedCountry);
        } else {
          setSelectedCountry(defaultCountry);
        }
      } else {
        setSelectedCountry(defaultCountry);
      }
    } else {
      setCandidate(new CandidateId());
      setSelectedCountry(defaultCountry);
      setSkills([]);
    }

    setValidationErrors([]);
    setErrorMessages({});
    setProfileUrlVerification(null);
    setVerifiedProfileUrl('');
  };

  const handleFileChange = (info: any) => {
    if (info.file && info.file.status && info.file.status === 'removed') {
      setFile(null);
      clearFormData();
    } else if (info.file) {
      setFile(info.file);
      ExtractPersonalInfo(info.file);
    }
  };

  const uploadProps: UploadProps = {
    multiple: false,
    maxCount: 1,
    accept: '.pdf,.docx',
    beforeUpload(file) {
      setFileSpinner(true);
      const isPDF = file.type === 'application/pdf';
      const isDOCX =
        file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      if (!isPDF && !isDOCX) {
        message.error('Only PDF or DOCX files are allowed.');
        setFileSpinner(false);
        return Upload.LIST_IGNORE;
      }

      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        message.error('File size must be less than 10MB.');
        setFileSpinner(false);
        return Upload.LIST_IGNORE;
      }

      return false;
    },
    onChange: handleFileChange,
    onRemove() {
      setFile(null);
      clearFormData();
    },
  };

  const items = [
    {
      key: '1',
      label: (
        <span className="candidate-profile-tab">
          <UserOutlined />
          Profile
        </span>
      ),
      children: (
        <Container>
          <section className="candidate-section candidate-profile-form">
            <header className="candidate-section__header">
              <div>
                <span className="candidate-section__eyebrow">
                  Candidate record
                </span>
                <h2>Personal information</h2>
                <p>
                  Keep contact details, profile access, availability, and
                  recruiter rating up to date.
                </p>
              </div>
            </header>
            <div className="mb-8">
              <div className="flex flex-col lg:flex-row gap-8 items-stretch">
                {!candidateId && (
                  <div className="w-full lg:w-1/3 flex flex-col">
                    <h3 className="mb-4 text-lg font-semibold text-gray-800 text-center">
                      Upload a CV to fill candidate information
                      automatically{' '}
                    </h3>
                    <div className="flex-1 flex items-center">
                      <Dragger
                        {...uploadProps}
                        style={{
                          height: 'auto',
                        }}
                        className="w-full"
                        showUploadList={false}
                      >
                        {fileSpinner ? (
                          <div className="flex flex-col items-center gap-2">
                            <Spin style={{ fontSize: '40px' }} />
                            <p className="ant-upload-hint">
                              Processing candidate's CV, this may take a few
                              seconds...
                            </p>
                          </div>
                        ) : file ? (
                          <>
                            <p
                              className="ant-upload-drag-icon"
                              style={{ marginBottom: 0 }}
                            >
                              <InboxOutlined style={{ fontSize: '40px' }} />
                            </p>
                            <p className="ant-upload-hint">
                              <strong>{file.name}</strong> uploaded successfully
                            </p>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginTop: 12,
                              }}
                            >
                              <AntButton
                                type="text"
                                danger
                                size="small"
                                onClick={() => {
                                  setFile(null);
                                  clearFormData();
                                }}
                              >
                                <DeleteOutlined /> Delete
                              </AntButton>
                            </div>
                          </>
                        ) : (
                          <>
                            <p
                              className="ant-upload-drag-icon"
                              style={{ marginBottom: 0 }}
                            >
                              <InboxOutlined style={{ fontSize: '40px' }} />
                            </p>
                            <p className="ant-upload-text">
                              Drag & Drop or browse CV
                            </p>
                            <p className="ant-upload-hint">
                              Supports: PDF & DOCX
                            </p>
                          </>
                        )}
                      </Dragger>
                    </div>
                  </div>
                )}
                <div className={candidateId ? 'w-full' : 'w-full lg:w-2/3'}>
                  <div className="candidate-profile-fields grid grid-cols-1 lg:grid-cols-2 gap-x-5 gap-y-5">
                    <div className="candidate-profile-field h-10 mb-4">
                      {candidateId && (
                        <span className="text-left block text-sm font-medium text-gray-600 my-2">
                          First Name
                        </span>
                      )}
                      <Input
                        placeholder="First Name"
                        name="firstname"
                        autoFocus={true}
                        value={candidate.firstname}
                        onChange={handleInputChange}
                        className="rounded-lg"
                        status={
                          validationErrors.includes('firstname') ? 'error' : ''
                        }
                        disabled={isDeleted || candidate.blocked}
                      />
                      <div className="h-4 mt-1">
                        {validationErrors.includes('firstname') && (
                          <span className="text-left block text-red-500 text-xs">
                            {errorMessages.firstname}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="candidate-profile-field flex flex-col h-10 mb-4">
                      {candidateId && (
                        <span className="text-left block text-sm font-medium text-gray-600 my-2">
                          Last Name
                        </span>
                      )}
                      <Input
                        placeholder="Last Name"
                        name="lastname"
                        value={candidate.lastname}
                        onChange={handleInputChange}
                        className="rounded-lg"
                        status={
                          validationErrors.includes('lastname') ? 'error' : ''
                        }
                        disabled={isDeleted || candidate.blocked}
                      />
                      <div className="h-4 mt-1">
                        {validationErrors.includes('lastname') && (
                          <span className="text-left block text-red-500 text-xs">
                            {errorMessages.lastname}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="candidate-profile-field candidate-profile-field--email flex flex-col h-10 mb-4">
                      {candidateId && (
                        <span className="text-left block text-sm font-medium text-gray-600 my-2">
                          Email
                        </span>
                      )}
                      <div className="flex flex-row gap-3">
                        <div className="flex-1">
                          <Input
                            placeholder="Email"
                            name="email"
                            value={candidate.email}
                            onChange={handleInputChange}
                            className="rounded-lg"
                            status={
                              validationErrors.includes('email') ? 'error' : ''
                            }
                            disabled={isDeleted || candidate.blocked}
                          />
                        </div>
                        <Tooltip title="Send Email">
                          <AntButton
                            aria-label="Send email"
                            className="candidate-profile-field-action"
                            icon={<MailOutlined />}
                            onClick={() =>
                              window.open(
                                `mailto:${candidate.email}`,
                                '_blank'
                              ) ||
                              (window.location.href = `mailto:${candidate.email}`)
                            }
                            disabled={isDeleted || candidate.blocked}
                          />
                        </Tooltip>
                      </div>
                      <div className="h-4 mt-1">
                        {validationErrors.includes('email') && (
                          <span className="text-left block text-red-500 text-xs">
                            {errorMessages.email}
                          </span>
                        )}
                      </div>
                      {candidate.alternateEmails?.length ? (
                        <div className="candidate-profile__alternate-emails">
                          <span>Alternate:</span>
                          {candidate.alternateEmails.map((email) => (
                            <Tag key={email}>{email}</Tag>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    <div className="candidate-profile-field candidate-profile-field--phone flex flex-col h-10 mb-4">
                      {candidateId && (
                        <span className="text-left block text-sm font-medium text-gray-600 my-2">
                          Phone Number
                        </span>
                      )}
                      <div className="flex flex-row gap-2">
                        <div>
                          <Select
                            style={{
                              width: '100%',
                              height: '32px',
                            }}
                            className="rounded-lg"
                            options={countryOptions}
                            onChange={(value) => {
                              const option = countryOptions.find(
                                (opt) => opt.value === value
                              );
                              handleCountryChange(option || null);
                            }}
                            placeholder="Select a country"
                            value={selectedCountry?.value}
                            disabled={isDeleted || candidate.blocked}
                          />
                        </div>
                        <div className="flex-1">
                          <Input
                            placeholder="Phone Number"
                            type="text"
                            name="phoneNumber"
                            value={
                              candidate.phoneNumber && selectedCountry
                                ? candidate.phoneNumber.replace(
                                    new RegExp(
                                      `^\\+?${selectedCountry.dial_code}`
                                    ),
                                    ''
                                  )
                                : candidate.phoneNumber || ''
                            }
                            onChange={handleInputChange}
                            onKeyPress={(e) => {
                              if (!/[0-9]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            className="rounded-lg"
                            status={
                              validationErrors.includes('phoneNumber')
                                ? 'error'
                                : ''
                            }
                            disabled={isDeleted || candidate.blocked}
                          />
                        </div>
                        <Tooltip title="WhatsApp">
                          <AntButton
                            aria-label="Open WhatsApp"
                            className="candidate-profile-field-action"
                            icon={<WhatsAppOutlined />}
                            onClick={() =>
                              window.open(
                                `https://wa.me/${candidate.phoneNumber?.replace(/\D/g, '')}`,
                                '_blank'
                              )
                            }
                            disabled={isDeleted || candidate.blocked}
                          />
                        </Tooltip>
                      </div>
                      <div className="h-4 mt-1">
                        {validationErrors.includes('phoneNumber') && (
                          <span className="text-left block text-red-500 text-xs">
                            {errorMessages.phoneNumber}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="candidate-profile-field candidate-profile-field--profile-url flex flex-col h-10 mb-4">
                      {candidateId && (
                        <span className="text-left block text-sm font-medium text-gray-600 my-2">
                          Profile URL
                        </span>
                      )}
                      <div className="flex flex-row gap-3">
                        <div className="flex-1">
                          <Input
                            placeholder="Profile URL"
                            name="profileURL"
                            value={candidate.profileURL}
                            onChange={handleInputChange}
                            className="rounded-lg"
                            status={
                              validationErrors.includes('profileURL')
                                ? 'error'
                                : ''
                            }
                            disabled={isDeleted || candidate.blocked}
                          />
                        </div>
                        <Tooltip title="Verify profile URL">
                          <AntButton
                            aria-label="Verify profile URL"
                            className="candidate-profile-field-action"
                            icon={<SafetyCertificateOutlined />}
                            loading={isVerifyingProfileUrl}
                            onClick={() => void verifyProfileUrl()}
                            disabled={
                              isDeleted ||
                              candidate.blocked ||
                              !candidate.profileURL?.trim()
                            }
                          />
                        </Tooltip>
                        <Tooltip title="View Profile URL">
                          <AntButton
                            aria-label="Open profile URL"
                            className="candidate-profile-field-action"
                            icon={<LinkOutlined />}
                            onClick={() => {
                              window.open(candidate.profileURL, '_blank');
                            }}
                            disabled={
                              isDeleted ||
                              candidate.blocked ||
                              !candidate.profileURL?.trim()
                            }
                          />
                        </Tooltip>
                      </div>
                      <div className="candidate-profile-url-feedback mt-1">
                        {profileUrlVerification &&
                        verifiedProfileUrl === candidate.profileURL?.trim() ? (
                          <span
                            role="status"
                            className={`candidate-profile-url-status candidate-profile-url-status--${profileUrlVerification.status.toLowerCase()}`}
                          >
                            {profileUrlVerification.status === 'VERIFIED' ? (
                              <CheckCircleFilled />
                            ) : profileUrlVerification.acceptable ? (
                              <WarningFilled />
                            ) : (
                              <CloseCircleFilled />
                            )}
                            <span>
                              {profileUrlVerification.statusCode
                                ? `HTTP ${profileUrlVerification.statusCode} · `
                                : ''}
                              {profileUrlVerification.message}
                            </span>
                          </span>
                        ) : validationErrors.includes('profileURL') ? (
                          <span className="text-left block text-red-500 text-xs">
                            {errorMessages.profileURL}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="candidate-profile-preferences">
                      <span>Recruiting controls</span>
                      {isAdmin ? (
                        <>
                          <label>
                            <Checkbox
                              checked={candidate.active}
                              onChange={(event) =>
                                setCandidate((previous) => ({
                                  ...previous,
                                  active: event.target.checked,
                                }))
                              }
                              disabled={isDeleted || candidate.blocked}
                            />
                            <div>
                              <strong>Active candidate</strong>
                              <small>Available for current searches</small>
                            </div>
                          </label>
                          <label>
                            <Checkbox
                              checked={pendingBlocked}
                              onChange={(event) =>
                                setPendingBlocked(event.target.checked)
                              }
                              disabled={!isAdmin}
                            />
                            <div>
                              <strong>Blacklist candidate</strong>
                              <small>Exclude from recruiter workflows</small>
                            </div>
                          </label>
                        </>
                      ) : null}
                    </div>

                    <div className="candidate-profile-rating">
                      <div>
                        <span>Recruiter rating</span>
                        <small>Internal evaluation visible to your team</small>
                      </div>
                      <ClickOnlyRate
                        value={candidate.rate ?? null}
                        disabled={isDeleted || candidate.blocked}
                        allowClear
                        sizePx={22}
                        activeColor={jo.starFilled}
                        inactiveColor={jo.starEmpty}
                        onChange={(val) => {
                          if (val === null || val === 0) {
                            setCandidate((prev) => ({ ...prev, rate: null }));
                          } else if (
                            Number.isInteger(val) &&
                            val >= 1 &&
                            val <= 5
                          ) {
                            setCandidate((prev) => ({
                              ...prev,
                              rate: val as number,
                            }));
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!isDeleted && (
              <div className="candidate-profile-savebar">
                <div>
                  <strong>
                    {isButtonDisabled
                      ? 'Profile is up to date'
                      : 'Unsaved changes'}
                  </strong>
                  <span>
                    {isButtonDisabled
                      ? 'Changes will appear across the hiring workspace.'
                      : 'Review the information before saving.'}
                  </span>
                </div>
                <div className="candidate-profile-savebar__actions">
                  <AntButton
                    className="candidate-profile-savebar__discard"
                    disabled={isButtonDisabled}
                    onClick={clearFormData}
                  >
                    Discard
                  </AntButton>
                  <AntButton
                    className="candidate-profile-savebar__submit"
                    type="primary"
                    icon={<SaveOutlined />}
                    disabled={isButtonDisabled || isVerifyingProfileUrl}
                    onClick={handleSave}
                  >
                    {candidateId ? 'Update profile' : 'Create candidate'}
                  </AntButton>
                </div>
              </div>
            )}
          </section>
        </Container>
      ),
    },
    ...(candidateId
      ? [
          {
            key: '5',
            label: (
              <span className="candidate-profile-tab">
                <RobotOutlined />
                AI Summary
              </span>
            ),
            children: (
              <CandidateAiSummary
                candidateId={candidateId}
                candidateName={
                  `${candidate.firstname || ''} ${candidate.lastname || ''}`.trim() ||
                  'Candidate'
                }
                refreshToken={aiSummaryRefreshToken}
                hasUnsavedProfileChanges={!isDataUnchanged}
              />
            ),
          },
          {
            key: '2',
            label: (
              <span className="candidate-profile-tab">
                <FileTextOutlined />
                Files
              </span>
            ),
            children: (
              <CandidateFilesTable
                key={candidate.blocked ? 'blocked' : 'unblocked'}
                ref={filesTableRef}
                isDeleted={isDeleted}
                isBlocked={candidate.blocked}
              />
            ),
          },
          {
            key: '3',
            label: (
              <span className="candidate-profile-tab">
                <ToolOutlined />
                Skills
                {skills.length ? <em>{skills.length}</em> : null}
              </span>
            ),
            children: <CandidateSkills skills={skills} />,
          },
          {
            key: '4',
            label: (
              <span className="candidate-profile-tab">
                <SolutionOutlined />
                Job Applications
              </span>
            ),
            children: <JobApplicationTableByCandidate />,
          },
        ]
      : []),
  ];

  const candidateName =
    `${candidate.firstname || ''} ${candidate.lastname || ''}`.trim() ||
    'New candidate';
  const profileFields = [
    candidate.firstname,
    candidate.lastname,
    candidate.email,
    candidate.phoneNumber,
    candidate.profileURL,
    skills.length ? 'skills' : '',
  ];
  const profileCompletion = Math.round(
    (profileFields.filter(Boolean).length / profileFields.length) * 100
  );
  const candidateStatus = isDeleted
    ? 'Archived'
    : pendingBlocked
      ? 'Blacklisted'
      : candidate.active
        ? 'Active'
        : 'Inactive';
  const displayPhone = formatProfilePhone(
    candidate.phoneNumber,
    selectedCountry?.dial_code
  );

  if (candidateLoading) {
    return (
      <div
        className="candidate-profile-loading"
        role="status"
        aria-label="Loading candidate profile"
        aria-live="polite"
      >
        <div>
          <Spin size="large" />
          <strong>Loading candidate profile</strong>
          <span>
            Retrieving profile details, files, skills, and application history.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="candidate-profile-page">
      <section className="candidate-profile-hero">
        <button
          type="button"
          className="candidate-profile-hero__back"
          onClick={GoBack}
        >
          <ArrowLeftOutlined />
          Back to candidates
        </button>
        <div className="candidate-profile-hero__main">
          <Avatar
            size={68}
            icon={
              !candidate.firstname && !candidate.lastname ? (
                <UserOutlined />
              ) : undefined
            }
          >
            {candidate.firstname?.charAt(0)}
            {candidate.lastname?.charAt(0)}
          </Avatar>
          <div className="candidate-profile-hero__identity">
            <span>Candidate profile</span>
            <h1>{candidateName}</h1>
            <div>
              <Tag
                icon={
                  candidateStatus === 'Active' ? (
                    <CheckCircleFilled />
                  ) : (
                    <StopOutlined />
                  )
                }
                color={candidateStatus === 'Active' ? 'blue' : 'default'}
              >
                {candidateStatus}
              </Tag>
              {candidate.email ? <span>{candidate.email}</span> : null}
              {displayPhone ? <span>{displayPhone}</span> : null}
            </div>
          </div>
          <div className="candidate-profile-hero__completion">
            <div>
              <strong>{profileCompletion}%</strong>
              <span>Profile complete</span>
            </div>
            <span>
              <i style={{ width: `${profileCompletion}%` }} />
            </span>
          </div>
          {candidateId ? (
            <div className="candidate-profile-hero__actions">
              <Tooltip title="Send email">
                <AntButton
                  icon={<MailOutlined />}
                  disabled={!candidate.email}
                  onClick={() => {
                    window.location.href = `mailto:${candidate.email}`;
                  }}
                >
                  Email
                </AntButton>
              </Tooltip>
              <Tooltip title="Open WhatsApp">
                <AntButton
                  icon={<WhatsAppOutlined />}
                  disabled={!candidate.phoneNumber}
                  onClick={() =>
                    window.open(
                      `https://wa.me/${candidate.phoneNumber?.replace(/\D/g, '')}`,
                      '_blank'
                    )
                  }
                >
                  WhatsApp
                </AntButton>
              </Tooltip>
              {candidate.profileURL ? (
                <Tooltip title="Open external profile">
                  <AntButton
                    type="primary"
                    icon={<LinkOutlined />}
                    onClick={() => window.open(candidate.profileURL, '_blank')}
                  >
                    View profile
                  </AntButton>
                </Tooltip>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>
      <Tabs
        className="candidate-profile-tabs"
        defaultActiveKey={candidateId ? activeTab : '1'}
        items={items}
        tabBarGutter={tabGutter}
        onChange={(key) => {
          if (key === '5') {
            setAiSummaryRefreshToken((current) => current + 1);
          }
        }}
      />
    </div>
  );
};

export default CandidateProfile;
